import { useState } from "react";
import { api } from "@/lib/api";
import { X, Download } from "lucide-react";
import EeCategoryImport from "@/components/admin/products/EeCategoryImport";

// ---------- ee-turbo.eu single product import modal ----------
export default function EeTurboImportModal({ state, onChange, onClose, onImported }) {
    const [tab, setTab] = useState("single"); // "single" | "bulk"
    const [bulkText, setBulkText] = useState("");
    const [bulkRunning, setBulkRunning] = useState(false);
    const [bulkOverwrite, setBulkOverwrite] = useState(false);
    const [bulkResult, setBulkResult] = useState(null);
    const [bulkError, setBulkError] = useState("");

    const set = (patch) => onChange({ ...state, ...patch });

    const doPreview = async () => {
        if (!state.code?.trim()) return;
        set({ loading: true, error: "", preview: null, result: null });
        try {
            const { data } = await api.post("/admin/ee-turbo-import", {
                code: state.code.trim(),
                dry_run: true,
            });
            if (!data.found) {
                set({ loading: false, error: data.error || "Product not found", preview: null });
                return;
            }
            set({ loading: false, preview: data, error: "" });
        } catch (e) {
            set({ loading: false, error: e.response?.data?.detail || "Preview failed", preview: null });
        }
    };

    const doImport = async (overwrite = false) => {
        set({ loading: true, error: "", result: null });
        try {
            const { data } = await api.post("/admin/ee-turbo-import", {
                code: state.code.trim(),
                overwrite,
            });
            if (!data.found) {
                set({ loading: false, error: data.error || "Product not found" });
                return;
            }
            if (data.imported) {
                set({ loading: false, result: data, error: "" });
                setTimeout(() => onImported?.(), 1200);
                return;
            }
            set({ loading: false, result: data, error: "" });
        } catch (e) {
            set({ loading: false, error: e.response?.data?.detail || "Import failed" });
        }
    };

    const p = state.preview?.product;
    const r = state.result;

    const parsedCodes = bulkText
        .split(/[\n,;\t]+/)
        .map((s) => s.trim())
        .filter(Boolean);

    const runBulk = async () => {
        if (parsedCodes.length === 0) return;
        setBulkRunning(true);
        setBulkError("");
        setBulkResult(null);
        try {
            const { data } = await api.post("/admin/ee-turbo-import/bulk", {
                codes: parsedCodes,
                overwrite: bulkOverwrite,
            });
            setBulkResult(data);
        } catch (e) {
            setBulkError(e.response?.data?.detail || "Bulk import failed");
        } finally {
            setBulkRunning(false);
        }
    };

    const statusColor = (s) => ({
        created: "bg-emerald-100 text-emerald-800 border-emerald-300",
        updated: "bg-sky-100 text-sky-800 border-sky-300",
        skipped: "bg-slate-100 text-slate-700 border-slate-300",
        not_found: "bg-amber-100 text-amber-800 border-amber-300",
        failed: "bg-red-100 text-red-800 border-red-300",
    })[s] || "bg-slate-100 text-slate-700 border-slate-300";

    return (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="ee-import-modal">
            <div className="bg-white max-w-3xl w-full max-h-[90vh] flex flex-col">
                <div className="p-4 border-b border-slate-200 flex items-center justify-between">
                    <div>
                        <div className="label-kicker text-slate-500 mb-0.5">Import from ee-turbo.eu</div>
                        <div className="font-display font-black text-xl uppercase">Product catalog import</div>
                    </div>
                    <button onClick={onClose} data-testid="ee-import-close"><X className="w-5 h-5" /></button>
                </div>

                {/* Tabs */}
                <div className="flex border-b border-slate-200 bg-slate-50">
                    <button
                        onClick={() => setTab("single")}
                        className={`px-4 py-2 text-xs font-display font-bold uppercase tracking-wider border-b-2 ${
                            tab === "single"
                                ? "border-[hsl(var(--primary))] text-slate-900 bg-white"
                                : "border-transparent text-slate-500 hover:text-slate-900"
                        }`}
                        data-testid="ee-tab-single"
                    >
                        Single code
                    </button>
                    <button
                        onClick={() => setTab("bulk")}
                        className={`px-4 py-2 text-xs font-display font-bold uppercase tracking-wider border-b-2 ${
                            tab === "bulk"
                                ? "border-[hsl(var(--primary))] text-slate-900 bg-white"
                                : "border-transparent text-slate-500 hover:text-slate-900"
                        }`}
                        data-testid="ee-tab-bulk"
                    >
                        Bulk list
                    </button>
                    <button
                        onClick={() => setTab("category")}
                        className={`px-4 py-2 text-xs font-display font-bold uppercase tracking-wider border-b-2 ${
                            tab === "category"
                                ? "border-[hsl(var(--primary))] text-slate-900 bg-white"
                                : "border-transparent text-slate-500 hover:text-slate-900"
                        }`}
                        data-testid="ee-tab-category"
                    >
                        Sipas kategorisë
                    </button>
                </div>

                <div className="p-4 space-y-4 overflow-auto flex-1">
                    {tab === "single" && (
                        <SingleImportBody
                            state={state}
                            set={set}
                            p={p}
                            r={r}
                            doPreview={doPreview}
                            doImport={doImport}
                        />
                    )}

                    {tab === "category" && <EeCategoryImport onImported={onImported} />}

                    {tab === "bulk" && (
                        <div className="space-y-3">
                            <div>
                                <label className="label-kicker mb-1 block">Product codes (one per line, or comma/semicolon separated)</label>
                                <textarea
                                    value={bulkText}
                                    onChange={(e) => setBulkText(e.target.value)}
                                    rows={8}
                                    className="input-field font-mono text-sm"
                                    placeholder="AC-K050&#10;Turbo-K028&#10;GT15-017&#10;BP-BW1-1B"
                                    data-testid="ee-bulk-textarea"
                                    disabled={bulkRunning}
                                />
                                <div className="mt-1 text-[11px] font-mono text-slate-500 flex items-center justify-between">
                                    <span>{parsedCodes.length} unique code{parsedCodes.length === 1 ? "" : "s"}</span>
                                    <span>Max 200 codes per request</span>
                                </div>
                            </div>

                            <label className="flex items-center gap-2 text-sm cursor-pointer" data-testid="ee-bulk-overwrite-label">
                                <input
                                    type="checkbox"
                                    checked={bulkOverwrite}
                                    onChange={(e) => setBulkOverwrite(e.target.checked)}
                                    disabled={bulkRunning}
                                    className="w-4 h-4 accent-[hsl(var(--primary))]"
                                    data-testid="ee-bulk-overwrite"
                                />
                                <span>Overwrite existing products (matched by part number in same category)</span>
                            </label>

                            {bulkError && (
                                <div className="bg-red-50 border border-red-200 text-red-700 text-xs font-mono p-2">{bulkError}</div>
                            )}

                            {bulkResult && (
                                <div className="space-y-2" data-testid="ee-bulk-result">
                                    <div className="flex flex-wrap gap-2 text-xs font-mono">
                                        <span className="px-2 py-1 bg-emerald-100 text-emerald-800 border border-emerald-300">
                                            ✓ {bulkResult.counters.created} created
                                        </span>
                                        <span className="px-2 py-1 bg-sky-100 text-sky-800 border border-sky-300">
                                            ⟳ {bulkResult.counters.updated} updated
                                        </span>
                                        <span className="px-2 py-1 bg-slate-100 text-slate-700 border border-slate-300">
                                            · {bulkResult.counters.skipped} skipped
                                        </span>
                                        <span className="px-2 py-1 bg-amber-100 text-amber-800 border border-amber-300">
                                            ? {bulkResult.counters.not_found} not found
                                        </span>
                                        <span className="px-2 py-1 bg-red-100 text-red-800 border border-red-300">
                                            × {bulkResult.counters.failed} failed
                                        </span>
                                        <span className="px-2 py-1 border border-slate-300 ml-auto font-bold">
                                            total {bulkResult.total}
                                        </span>
                                    </div>

                                    <div className="max-h-[320px] overflow-auto border border-slate-200">
                                        <table className="w-full text-xs font-mono">
                                            <thead className="bg-slate-100 sticky top-0">
                                                <tr>
                                                    <th className="px-2 py-1 text-left w-1/4">Code</th>
                                                    <th className="px-2 py-1 text-left w-1/6">Status</th>
                                                    <th className="px-2 py-1 text-left">Details</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {bulkResult.results.map((row, i) => (
                                                    <tr key={i} className="border-t border-slate-100" data-testid={`ee-bulk-row-${i}`}>
                                                        <td className="px-2 py-1">{row.code}</td>
                                                        <td className="px-2 py-1">
                                                            <span className={`px-1.5 py-0.5 border ${statusColor(row.status)}`}>
                                                                {row.status}
                                                            </span>
                                                        </td>
                                                        <td className="px-2 py-1 text-slate-600">
                                                            {row.name ? `${row.name} · ${row.category || ""}` : row.reason || row.error || "—"}
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </div>

                <div className="p-4 border-t border-slate-200 flex justify-end gap-2">
                    <button onClick={onClose} className="btn-outline" data-testid="ee-import-cancel">
                        Close
                    </button>
                    {tab === "single" && p && !r?.imported && (
                        <button
                            onClick={() => doImport(false)}
                            disabled={state.loading}
                            className="btn-primary"
                            data-testid="ee-import-confirm"
                        >
                            <Download className="w-4 h-4" /> {state.loading ? "Importing..." : "Import to catalog"}
                        </button>
                    )}
                    {tab === "bulk" && (
                        <button
                            onClick={() => {
                                runBulk();
                                if (parsedCodes.length > 0) {
                                    setTimeout(() => onImported?.(), 100);
                                }
                            }}
                            disabled={bulkRunning || parsedCodes.length === 0}
                            className="btn-primary"
                            data-testid="ee-bulk-run"
                        >
                            <Download className="w-4 h-4" />
                            {bulkRunning
                                ? `Importing ${parsedCodes.length} codes...`
                                : `Import ${parsedCodes.length} code${parsedCodes.length === 1 ? "" : "s"}`}
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
}

function SingleImportBody({ state, set, p, r, doPreview, doImport }) {
    return (
        <>
            <div className="flex items-end gap-2">
                <div className="flex-1">
                    <label className="label-kicker mb-1 block">Product code / SKU</label>
                    <input
                        value={state.code}
                        onChange={(e) => set({ code: e.target.value, preview: null, result: null, error: "" })}
                        onKeyDown={(e) => e.key === "Enter" && doPreview()}
                        className="input-field font-mono"
                        placeholder="e.g. AC-K050, Turbo-K028, GT15-017"
                        data-testid="ee-import-code-input"
                        autoFocus
                    />
                </div>
                <button
                    onClick={doPreview}
                    disabled={state.loading || !state.code?.trim()}
                    className="btn-outline"
                    data-testid="ee-import-preview-button"
                >
                    {state.loading ? "Loading..." : "Preview"}
                </button>
            </div>

            {state.error && (
                <div className="bg-red-50 border border-red-200 text-red-700 text-xs font-mono p-2" data-testid="ee-import-error">
                    {state.error}
                </div>
            )}

            {r?.imported && (
                <div className="bg-emerald-50 border border-emerald-200 text-emerald-800 text-sm p-3" data-testid="ee-import-success">
                    ✓ Product {r.action} successfully · id {r.product_id.slice(0, 8)}…
                </div>
            )}

            {r && !r.imported && r.reason && (
                <div className="bg-amber-50 border border-amber-300 text-amber-900 text-sm p-3 flex items-start justify-between gap-3" data-testid="ee-import-exists">
                    <div>
                        ⚠ {r.reason}
                        <div className="text-xs font-mono mt-1 text-amber-700">
                            existing id: {r.existing_product_id?.slice(0, 8)}…
                        </div>
                    </div>
                    <button onClick={() => doImport(true)} className="btn-primary shrink-0" data-testid="ee-import-overwrite">
                        Overwrite
                    </button>
                </div>
            )}

            {p && !r?.imported && (
                <div className="border border-slate-200 p-4 space-y-3" data-testid="ee-import-preview">
                    <div className="label-kicker text-slate-500">Preview</div>
                    <div className="grid grid-cols-[1fr_2fr] gap-3 text-sm">
                        <div className="text-slate-500 font-mono">Name</div>
                        <div className="font-display font-bold uppercase">{p.name}</div>

                        <div className="text-slate-500 font-mono">Part number</div>
                        <div className="font-mono">{p.part_number}</div>

                        <div className="text-slate-500 font-mono">Category</div>
                        <div>
                            {state.preview.category.name}{" "}
                            {state.preview.category.created && (
                                <span className="text-[10px] font-mono text-emerald-700 bg-emerald-50 border border-emerald-200 px-1">
                                    new category will be created
                                </span>
                            )}
                        </div>

                        <div className="text-slate-500 font-mono">Images</div>
                        <div>{p.images_count} {p.images_count === 1 ? "image" : "images"}</div>

                        {p.note && (
                            <>
                                <div className="text-slate-500 font-mono">Note</div>
                                <div className="text-slate-800 whitespace-pre-line">{p.note}</div>
                            </>
                        )}
                        {p.description && (
                            <>
                                <div className="text-slate-500 font-mono">Description</div>
                                <div className="text-slate-800 text-xs line-clamp-3">{p.description}</div>
                            </>
                        )}
                        {p.usage_count > 0 && (
                            <>
                                <div className="text-slate-500 font-mono">Usage rows</div>
                                <div>{p.usage_count} vehicle row{p.usage_count === 1 ? "" : "s"}</div>
                            </>
                        )}
                    </div>

                    {p.image_url && (
                        <div className="flex gap-2 flex-wrap pt-2 border-t border-slate-100">
                            <img src={p.image_url} alt="" className="w-20 h-20 object-contain border border-slate-200 bg-white" />
                            {p.gallery.slice(0, 4).map((g, i) => (
                                <img key={i} src={g} alt="" className="w-20 h-20 object-contain border border-slate-200 bg-white" />
                            ))}
                        </div>
                    )}
                </div>
            )}
        </>
    );
}

